<template>
  <modal
    v-if="visible"
    :visible="visible"
    @click-outside-modal="$emit('close')"
  >
    <div class="flex justify-center px-4">
      <div class="tablet relative rounded bg-white p-8">
        <img
          class="absolute right-4 top-4 h-4 w-4 cursor-pointer"
          src="@/assets/close-icon.svg"
          alt="close icon"
          @click="$emit('close')"
        />

        <h3 class="text-left text-3xl text-dark-gray">
          {{
            fieldType === 'phone'
              ? 'Add New Phone'
              : fieldType === 'email'
                ? 'Add New Email'
                : 'Add New Contact'
          }}
        </h3>
        <p class="my-4 text-left text-body-color">
          Enter
          {{
            fieldType === 'phone'
              ? 'a phone number for'
              : fieldType === 'email'
                ? 'an email address for'
                : 'the name of'
          }}
          your {{ contactIsType }} contact.
        </p>
        <div>
          <div v-if="fieldType === 'name'">
            <form-field
              id="contact-info-name"
              label="Name"
              :value="contact.name"
              placeholder="Enter name"
              :on-input="onInput"
              name="contact_name"
              :is-valid="!$store.state.contacts?.errors?.name"
              :error-message="
                errors?.name &&
                contact?.name?.length > 0 &&
                'Enter first and last name'
              "
              class=""
              @keyup.enter="handleEnterKey"
            />
            <p
              v-if="$store.state.contacts?.errors?.name"
              class="sm:position -mb-1 -mt-4 text-2xs text-red-500 sm:absolute sm:bottom-1 sm:mb-0"
            >
              This name already exists
            </p>
          </div>
          <div v-else-if="fieldType === 'phone'">
            <form-field
              id="contact-info-phone-number"
              ref="addContactInfoPhone"
              label="Phone #"
              :value="contact.phone_number"
              placeholder="Enter phone #"
              :on-input="onInput"
              name="phone_number"
              pattern="number"
              type="tel"
              :is-valid="!$store.state.contacts?.errors?.phone"
              class=""
              @keyup.enter="handleEnterKey"
            />
            <p
              v-if="$store.state.contacts?.errors?.phone"
              class="sm:position -mb-1 -mt-4 text-2xs text-red-500 sm:absolute sm:bottom-1 sm:mb-0"
            >
              This phone number already exists
            </p>
          </div>
          <div v-else>
            <form-field
              id="contact-info-email"
              label="Email"
              :value="contact.email"
              placeholder="Enter Email"
              :on-input="onInput"
              name="email"
              :is-valid="!$store.state.contacts?.errors?.email"
              class=""
              @keyup.enter="handleEnterKey"
            />
            <p
              v-if="$store.state.contacts?.errors?.email"
              class="sm:position -mb-1 -mt-4 text-2xs text-red-500 sm:absolute sm:bottom-1 sm:mb-0 sm:ml-2"
            >
              This email already exists
            </p>
          </div>
        </div>
        <button-main
          :disabled="isButtonDisabled"
          class="mt-3 md:mt-2"
          @click="handleSubmit"
        >
          Add
          {{
            fieldType === 'phone'
              ? 'phone'
              : fieldType === 'email'
                ? 'email'
                : 'name'
          }}
        </button-main>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '@/components/modals/Modal.vue';
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';
import { formatPhone } from '@/common/validators';
import { isEmail } from '@/common/validators';

export default {
  name: 'AddContactInfoModal',
  components: {
    Modal,
    ButtonMain,
    FormField,
  },
  props: {
    visible: Boolean,
    updateField: Function,
    contactIsType: String,
    fieldType: String,
  },
  emits: ['close'],
  setup(_, { emit }) {
    const onCancel = () => {
      // handle contact form data here
      emit('close');
    };

    return {
      onCancel,
    };
  },
  data() {
    return {
      contact: {
        type: this.contactIsType,
        phone_number: '',
        email: '',
        name: '',
      },
      errors: {
        name: true,
        phone: true,
        email: true,
      },
    };
  },
  computed: {
    isButtonDisabled() {
      if (this.fieldType === 'name') {
        return this.errors.name;
      } else if (this.fieldType === 'phone') {
        return this.errors.phone;
      } else {
        return this.errors.email;
      }
    },
  },
  mounted() {
    if (this.fieldType === 'name') {
      const findName = document.querySelector(`#contact-info-name`);
      findName.focus();
    } else if (this.fieldType === 'phone') {
      const findPhoneNumber = document.querySelector(
        `#contact-info-phone-number`
      );
      findPhoneNumber.focus();
    } else {
      const findEmail = document.querySelector(`#contact-info-email`);
      findEmail.focus();
    }
  },
  methods: {
    handleEnterKey() {
      if (!this.isButtonDisabled) {
        this.handleSubmit();
      }
    },
    onInput(e) {
      if (e.target.name.includes('contact_name')) {
        const value = e.target.value;
        this.contact['name'] = value?.toUpperCase();
        this.errors.name = this.validatePersonName(value);
      } else if (e.target.name.includes('phone_number')) {
        const value = formatPhone(e.target.value);
        this.contact[e.target.name] = value;
        if (
          ['us', 'mx', 'ca'].includes(
            this.$refs?.addContactInfoPhone?.elementData?.selectedCountryData
              ?.iso2
          )
        ) {
          this.errors.phone = value.length !== 12;
        } else {
          this.errors.phone =
            !this.$refs?.addContactInfoPhone?.elementData?.isValidNumber();
        }
      } else {
        this.contact[e.target.name] = e.target.value;
        this.errors.email = !isEmail(e.target.value);
      }
    },
    validatePersonName(name) {
      const words = name?.trim()?.split(' ');

      if (/\d/.test(name) || words?.length !== 2) {
        return true;
      }

      return false;
    },
    async handleSubmit() {
      let value =
        this.fieldType === 'phone'
          ? this.$refs?.addContactInfoPhone?.elementData?.getNumber() ||
            this.contact?.phone_number
          : this.fieldType === 'email'
            ? this.contact?.email
            : this.contact?.name;
      this.updateField({ fieldType: this.fieldType, value: value });
      this.onCancel();
    },
  },
};
</script>

<style scoped>
@media screen and (min-width: 768px) {
  .tablet {
    width: 500px;
  }
}
</style>
